import type { Monaco } from '@monaco-editor/react';
import {
  buildMonacoTokenRules,
  getEditorColorScheme,
  type EditorColorSchemeTokenColors,
  type EditorColorSelection,
  type MonacoTokenRule,
} from './monacoColorSchemes';
// Type-only: `customEditorColors.ts` imports `JAM_MONACO_LIGHT`/`JAM_MONACO_DARK`
// (real values) from THIS module for its contrast helper. Keeping this edge
// `import type` means it is elided at compile time, so the two files form a
// DAG rather than a runtime cycle.
import type { CustomEditorColors } from './customEditorColors';

/**
 * Monaco surface theming (CH-10, GATE-1 finding 1) + editor color scheme
 * presets (Slice 1).
 *
 * Monaco's stock `vs-dark` paints `editor.background` `#1e1e1e` — a neutral
 * gray. Every neighbour in this app is navy (`--background` `#0f1624`,
 * `--panel` `#151c2b`, `--chrome` `#19202f`), so the editor pane read as a
 * foreign rectangle bolted into the shell. It was the only genuine surface
 * seam in the app; light mode was already fine.
 *
 * The fix REDEFINES the built-in `vs` / `vs-dark` themes rather than
 * registering new names. Monaco's `StandaloneThemeService.defineTheme`
 * explicitly supports this (`isBuiltinTheme(themeName)` branch), re-derives
 * inheriting themes, and refreshes the theme if it is currently active — so
 * ordering against editor mounts does not matter. Two reasons this beats a
 * `jam-dark` theme name:
 *
 *  1. every one of the app's ~20 Monaco mounts already passes
 *     `theme={darkMode ? 'vs-dark' : 'vs'}`; nothing has to be re-plumbed,
 *     and a future mount site is themed by default rather than by memory;
 *  2. the `.monaco-editor` element keeps its `vs-dark` / `vs` CSS class
 *     (`StandaloneTheme.id === name` for built-in names), which existing
 *     e2e specs assert on.
 *
 * A pure-CSS override could not have done the job: the minimap is drawn on a
 * canvas from `minimap.background`, so it is unreachable from a stylesheet.
 *
 * The background hexes MUST stay byte-identical to `--background` in
 * src/index.css / `THEME_BACKGROUND_COLORS` in electron/themeColors.ts —
 * monacoTheme.test.ts enforces that by reading index.html's splash colours,
 * which the existing themeColors.test.ts already chains to the CSS token.
 *
 * Editor color scheme presets (Slice 1) layer TOKEN colors on top of this —
 * `rules` now comes from `monacoColorSchemes.ts` (`EDITOR_COLOR_SCHEMES` +
 * `buildMonacoTokenRules`) rather than always being empty. Surfaces above
 * stay untouched: `defineTheme` is re-called with the same `colors`, only
 * `rules` changes, and Monaco's `StandaloneThemeService` refreshes any
 * currently-active editor live, so a scheme switch needs no mount-site
 * plumbing — see `setMonacoEditorColorScheme` below.
 */

/** `--background` light — the content canvas. */
const CANVAS_LIGHT = '#ffffff';
/** `--background` dark — also the colour Electron paints pre-render. */
const CANVAS_DARK = '#0f1624';

/** `--panel` per theme (line highlight / current-line band). */
const PANEL_LIGHT = '#f3f6fa';
const PANEL_DARK = '#151c2b';

/** `--surface-raised` per theme (suggest / find / hover widgets float). */
const RAISED_LIGHT = '#ffffff';
const RAISED_DARK = '#1b2331';

/** `--divider` per theme (widget seams). */
const DIVIDER_LIGHT = '#d8dfe6';
const DIVIDER_DARK = '#2b3341';

/**
 * `--primary` per theme, as 8-digit hex with the alpha the app's selection
 * washes use. Monaco's `Color.fromHex` accepts `#rrggbbaa`. Keeping these in
 * step with `--selection` means an editor selection and a grid selection are
 * visibly the same colour.
 */
const SELECTION_LIGHT = '#2b6d9e40';
const SELECTION_INACTIVE_LIGHT = '#2b6d9e1f';
const SELECTION_DARK = '#4d9bd652';
const SELECTION_INACTIVE_DARK = '#4d9bd629';

/** `--muted-foreground` per theme, for line numbers and indent guides. */
const MUTED_LIGHT = '#5f6873';
const MUTED_DARK = '#95a0ab';

type ThemeColors = Record<string, string>;

/**
 * Structurally assignable to Monaco's `IStandaloneThemeData` without pulling
 * the monaco-editor namespace into this module's public surface. `colors`
 * carries only the surface tokens `buildColors` sets — this theme never
 * touches syntax-highlighting colours through `colors`. `rules` holds the
 * active scheme's token-color rules (see the file header).
 */
interface MonacoThemeData {
  base: 'vs' | 'vs-dark';
  inherit: boolean;
  rules: MonacoTokenRule[];
  colors: ThemeColors;
}

function buildColors(
  canvas: string,
  panel: string,
  raised: string,
  divider: string,
  selection: string,
  inactiveSelection: string,
  muted: string,
): ThemeColors {
  return {
    'editor.background': canvas,
    // Gutter and minimap default to `editor.background` only when they are
    // not themselves themed; the built-in themes DO theme them, so both have
    // to be stated or the seam just moves to the left/right edge.
    'editorGutter.background': canvas,
    'minimap.background': canvas,
    'editorLineNumber.foreground': muted,
    'editor.lineHighlightBackground': panel,
    'editor.selectionBackground': selection,
    'editor.inactiveSelectionBackground': inactiveSelection,
    // Floating editor surfaces (find, suggest, hover, parameter hints) match
    // the app's popovers so a suggest list next to a Radix menu agrees.
    'editorWidget.background': raised,
    'editorWidget.border': divider,
    'editorSuggestWidget.background': raised,
    'editorSuggestWidget.border': divider,
    'editorHoverWidget.background': raised,
    'editorHoverWidget.border': divider,
  };
}

// The default scheme's rules — `JAM_MONACO_LIGHT` / `JAM_MONACO_DARK` are read
// directly by monacoTheme.test.ts (colors only), and by `defineThemesFor`
// below as the base object a non-default scheme's rules get spread over.
const DEFAULT_SCHEME = getEditorColorScheme('default');

export const JAM_MONACO_LIGHT: MonacoThemeData = {
  base: 'vs',
  inherit: true,
  rules: buildMonacoTokenRules(DEFAULT_SCHEME.light),
  colors: buildColors(
    CANVAS_LIGHT,
    PANEL_LIGHT,
    RAISED_LIGHT,
    DIVIDER_LIGHT,
    SELECTION_LIGHT,
    SELECTION_INACTIVE_LIGHT,
    MUTED_LIGHT,
  ),
};

export const JAM_MONACO_DARK: MonacoThemeData = {
  base: 'vs-dark',
  inherit: true,
  rules: buildMonacoTokenRules(DEFAULT_SCHEME.dark),
  colors: buildColors(
    CANVAS_DARK,
    PANEL_DARK,
    RAISED_DARK,
    DIVIDER_DARK,
    SELECTION_DARK,
    SELECTION_INACTIVE_DARK,
    MUTED_DARK,
  ),
};

// Which selection+tables are currently installed into Monaco's live `vs` /
// `vs-dark` themes (`null` = nothing defined yet), the selection/custom
// tables the app wants installed, and the Monaco instance captured from the
// most recent mount (so a later `setMonacoEditorColorScheme` call can
// redefine without waiting for another editor to mount).
//
// `appliedKey` (Slice 3) replaces a plain `EditorColorSchemeId` comparison:
// for a preset selection the key IS the id, but for `'custom'` the id alone
// can't tell "unchanged" from "the user tweaked one token", so the key folds
// in the custom tables too (`resolveAppliedKey` below) — the tables are a
// handful of hex strings, so a `JSON.stringify` per call is cheap.
let appliedKey: string | null = null;
let currentSelection: EditorColorSelection = 'default';
let currentCustomColors: CustomEditorColors | null = null;
let monacoInstance: Monaco | null = null;

function resolveAppliedKey(selection: EditorColorSelection, customColors: CustomEditorColors | null): string {
  return selection === 'custom' ? `custom:${JSON.stringify(customColors)}` : selection;
}

/**
 * The light/dark token tables to render for `selection`. For `'custom'`,
 * uses `customColors` when present, falling back to the `default` preset
 * when the user has never set any custom colors yet (e.g. a selection of
 * `'custom'` persisted without its `customEditorColors` sibling).
 */
function resolveTokenTables(
  selection: EditorColorSelection,
  customColors: CustomEditorColors | null,
): { light: EditorColorSchemeTokenColors; dark: EditorColorSchemeTokenColors } {
  if (selection === 'custom') {
    if (customColors) return customColors;
    const fallback = getEditorColorScheme('default');
    return { light: fallback.light, dark: fallback.dark };
  }
  return getEditorColorScheme(selection);
}

/**
 * The token rules the editor's `vs` / `vs-dark` theme is defined with, for a
 * given scheme selection — so a non-editor surface (the VQE SQL preview) can
 * colour Monaco tokens exactly like the editor, without mounting one.
 */
export function getEditorTokenRules(
  selection: EditorColorSelection,
  customColors: CustomEditorColors | null,
  isDark: boolean,
): MonacoTokenRule[] {
  const tables = resolveTokenTables(selection, customColors);
  return buildMonacoTokenRules(isDark ? tables.dark : tables.light);
}

/** `defineTheme` both built-ins with the resolved token rules. May throw. */
function defineThemesFor(monaco: Monaco, selection: EditorColorSelection, customColors: CustomEditorColors | null): void {
  const tables = resolveTokenTables(selection, customColors);
  monaco.editor.defineTheme('vs', { ...JAM_MONACO_LIGHT, rules: buildMonacoTokenRules(tables.light) });
  monaco.editor.defineTheme('vs-dark', { ...JAM_MONACO_DARK, rules: buildMonacoTokenRules(tables.dark) });
}

/**
 * Idempotently redefine `vs` / `vs-dark` onto the app's surface palette +
 * current editor color scheme.
 *
 * Call from every Monaco mount's `beforeMount` — Monaco is a singleton, so
 * only the first call does work, but wiring it everywhere means whichever
 * editor happens to mount first in a session gets themed. `ensure*` never
 * throws: a Monaco build that rejected the call must not take an editor
 * down over a colour.
 */
export function ensureJamMonacoThemes(monaco: Monaco): void {
  monacoInstance = monaco;
  const key = resolveAppliedKey(currentSelection, currentCustomColors);
  if (appliedKey === key) return;
  try {
    defineThemesFor(monaco, currentSelection, currentCustomColors);
    appliedKey = key;
  } catch {
    // Leave `appliedKey` stale so a later mount can retry.
  }
}

/**
 * Switches the editor color scheme (Settings → UI → Editor colors). Monaco's
 * `defineTheme` on a built-in name refreshes any currently-active editor
 * live, so every open Monaco mount re-colors immediately — no mount-site
 * plumbing needed. A no-op if no Monaco mount has captured an instance yet;
 * the next mount's `ensureJamMonacoThemes` picks up `currentSelection` then.
 *
 * `customColors` (Slice 3) is read only when passed: omit it (or pass
 * `undefined`) for a preset `selection` to leave whatever custom tables are
 * already tracked untouched — a preset switch doesn't need to know about
 * them. Pass the live tables (or explicit `null`) alongside `'custom'`.
 */
export function setMonacoEditorColorScheme(
  selection: EditorColorSelection,
  customColors?: CustomEditorColors | null,
): void {
  currentSelection = selection;
  if (customColors !== undefined) currentCustomColors = customColors;

  const key = resolveAppliedKey(currentSelection, currentCustomColors);
  if (!monacoInstance || appliedKey === key) return;
  try {
    defineThemesFor(monacoInstance, currentSelection, currentCustomColors);
    appliedKey = key;
  } catch {
    // Leave `appliedKey` stale so a later call can retry.
  }
}

/** Test-only: forget that the themes were installed and reset the scheme. */
export function resetJamMonacoThemesForTest(): void {
  appliedKey = null;
  currentSelection = 'default';
  currentCustomColors = null;
  monacoInstance = null;
}
